import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getMenusForPatient,
  getMenu,
  createMenu,
  updateMenu,
  softDeleteMenu,
  duplicateMenu,
  setActiveMenu,
  getMenuTemplates,
  createMenuTemplate,
  deleteMenuTemplate,
} from "../../firebase";

/**
 * Menus (תפריטים) + the clinic's reusable menu templates — nutrition clinics.
 *
 * Which menu is active lives on the PATIENT document, not on the menus, so
 * `setActiveMenu` also has to refresh whatever is showing the patient. The
 * patient doc itself is owned by patientsApi, so the component that calls it
 * invalidates that cache; here we only refresh the menu list and the patient's
 * ActiveMenu tag.
 */
export const menusApi = createApi({
  reducerPath: "menusApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Menus", "Menu", "MenuTemplates"],
  endpoints: (builder) => ({
    getMenusForPatient: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const menus = await getMenusForPatient(clinicId, patientId);
          return { data: menus };
        } catch (error) {
          console.error("[CK-7816] Error fetching menus:", {
            errorCode: "CK-7816",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "Menus", id: `PATIENT-${patientId}` },
      ],
    }),

    getMenu: builder.query({
      async queryFn({ clinicId, patientId, menuId }) {
        try {
          const menu = await getMenu(clinicId, patientId, menuId);
          return { data: menu };
        } catch (error) {
          console.error("[CK-7817] Error fetching menu:", {
            errorCode: "CK-7817",
            errorMessage: error.message,
            clinicId,
            patientId,
            menuId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { menuId }) => [{ type: "Menu", id: menuId }],
    }),

    createMenu: builder.mutation({
      async queryFn({ clinicId, patientId, menuData, therapistId, therapistName }) {
        try {
          const menu = await createMenu(clinicId, patientId, menuData, {
            therapistId,
            therapistName,
          });
          return { data: menu };
        } catch (error) {
          console.error("[CK-7818] Error creating menu:", {
            errorCode: "CK-7818",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Menus", id: `PATIENT-${patientId}` },
      ],
    }),

    updateMenu: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        menuId,
        menuData,
        therapistId,
        therapistName,
      }) {
        try {
          const menu = await updateMenu(clinicId, patientId, menuId, menuData, {
            therapistId,
            therapistName,
          });
          return { data: menu };
        } catch (error) {
          console.error("[CK-7831] Error updating menu:", {
            errorCode: "CK-7831",
            errorMessage: error.message,
            clinicId,
            patientId,
            menuId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId, menuId }) => [
        { type: "Menus", id: `PATIENT-${patientId}` },
        { type: "Menu", id: menuId },
      ],
    }),

    duplicateMenu: builder.mutation({
      async queryFn({ clinicId, patientId, menuId, therapistId, therapistName }) {
        try {
          const menu = await duplicateMenu(clinicId, patientId, menuId, {
            therapistId,
            therapistName,
          });
          return { data: menu };
        } catch (error) {
          console.error("[CK-7832] Error duplicating menu:", {
            errorCode: "CK-7832",
            errorMessage: error.message,
            clinicId,
            patientId,
            menuId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Menus", id: `PATIENT-${patientId}` },
      ],
    }),

    // Soft delete — clears activeMenuId server-side when this was the active menu
    deleteMenu: builder.mutation({
      async queryFn({ clinicId, patientId, menuId, reason, therapistId, therapistName }) {
        try {
          const result = await softDeleteMenu(clinicId, patientId, menuId, {
            therapistId,
            therapistName,
            reason,
          });
          return { data: result };
        } catch (error) {
          console.error("[CK-7833] Error deleting menu:", {
            errorCode: "CK-7833",
            errorMessage: error.message,
            clinicId,
            patientId,
            menuId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId, menuId }) => [
        { type: "Menus", id: `PATIENT-${patientId}` },
        { type: "Menu", id: menuId },
      ],
    }),

    // Writes patient.nutrition.activeMenuId. Pass menuId: null to clear it.
    setActiveMenu: builder.mutation({
      async queryFn({ clinicId, patientId, menuId, therapistId, therapistName }) {
        try {
          const result = await setActiveMenu(clinicId, patientId, menuId, {
            therapistId,
            therapistName,
          });
          return { data: result };
        } catch (error) {
          console.error("[CK-7834] Error setting the active menu:", {
            errorCode: "CK-7834",
            errorMessage: error.message,
            clinicId,
            patientId,
            menuId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Menus", id: `PATIENT-${patientId}` },
      ],
    }),

    // ===== Clinic-wide reusable templates =====

    getMenuTemplates: builder.query({
      async queryFn({ clinicId }) {
        try {
          const templates = await getMenuTemplates(clinicId);
          return { data: templates };
        } catch (error) {
          console.error("[CK-7835] Error fetching menu templates:", {
            errorCode: "CK-7835",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "MenuTemplates", id: "LIST" }],
    }),

    createMenuTemplate: builder.mutation({
      async queryFn({ clinicId, templateData, therapistId, therapistName }) {
        try {
          const template = await createMenuTemplate(clinicId, templateData, {
            therapistId,
            therapistName,
          });
          return { data: template };
        } catch (error) {
          console.error("[CK-7836] Error creating menu template:", {
            errorCode: "CK-7836",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "MenuTemplates", id: "LIST" }],
    }),

    deleteMenuTemplate: builder.mutation({
      async queryFn({ clinicId, templateId, name, therapistId, therapistName }) {
        try {
          const result = await deleteMenuTemplate(clinicId, templateId, {
            therapistId,
            therapistName,
            name,
          });
          return { data: result };
        } catch (error) {
          console.error("[CK-7837] Error deleting menu template:", {
            errorCode: "CK-7837",
            errorMessage: error.message,
            clinicId,
            templateId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "MenuTemplates", id: "LIST" }],
    }),
  }),
});

export const {
  useGetMenusForPatientQuery,
  useGetMenuQuery,
  useCreateMenuMutation,
  useUpdateMenuMutation,
  useDuplicateMenuMutation,
  useDeleteMenuMutation,
  useSetActiveMenuMutation,
  useGetMenuTemplatesQuery,
  useCreateMenuTemplateMutation,
  useDeleteMenuTemplateMutation,
} = menusApi;
